import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import { IconButton } from "./icon-button.tsx";

export type ToastType = "info" | "success" | "warning" | "error";

const accentByType: Record<ToastType, string> = {
  info: "var(--sf-primary)",
  success: "var(--sf-success)",
  warning: "var(--sf-warning)",
  error: "var(--sf-danger)",
};

export type ToastProps = HTMLAttributes<HTMLDivElement> & {
  type?: ToastType;
  title?: string;
  message?: string;
  icon?: ReactNode;
  onClose: () => void;
  closeLabel?: string;
};

export function Toast({
  type = "info",
  title,
  message,
  icon,
  onClose,
  closeLabel = "Fermer",
  className,
  ...props
}: ToastProps) {
  const accent = accentByType[type];

  return (
    <div
      className={cn(
        "pointer-events-auto flex min-w-[280px] max-w-[380px] items-start gap-2.5",
        "rounded-lg border border-[var(--sf-border)] bg-[var(--sf-surface)] p-3.5",
        "shadow-[var(--sf-shadow-lg)] animate-[sf-toast-in_250ms_ease-out]",
        className,
      )}
      style={{ borderLeftWidth: 3, borderLeftColor: accent }}
      {...props}
    >
      {icon && (
        <div className="mt-0.5 shrink-0" style={{ color: accent }}>
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        {title && (
          <div className="text-[13px] font-semibold text-[var(--sf-text)]">
            {title}
          </div>
        )}
        {message && (
          <p className="text-[12.5px] leading-snug text-[var(--sf-text-secondary)]">
            {message}
          </p>
        )}
      </div>
      <IconButton
        size="sm"
        aria-label={closeLabel}
        onClick={onClose}
        className="shrink-0"
      >
        <span className="text-xs leading-none">×</span>
      </IconButton>
    </div>
  );
}
